import { Heart, MessageCircle } from "lucide-react";
import {
  occurrenceEngagementStore,
  useIsOccurrenceLikePending,
  useIsOccurrenceReactionPending,
} from "../hooks/useOccurrenceEngagement";
import { REACTIONS } from "../constants/reactions";
import ReactionPickerButton from "./ReactionPickerButton";
import ReactionCounts from "./ReactionCounts";

// The engagement strip for one event occurrence — like, comment count,
// reaction counts and the reaction picker — laid out like a post card's action
// row (PostCard, PostStatic). Used under Home's event-start card and in the
// occurrence preview; both read `engagement` from occurrenceEngagementStore,
// so an action in one shows in the other.
//
// Follows the backend's rules: new likes and reactions only while
// `engagement.can_engage`; after a cancellation the viewer can still take
// back their own like or reaction. `onOpenComments` makes the comment count a
// button (omitted where the comments are already shown). `onError(err,
// action)` hears about rejected requests; the store has already rolled back.
export default function OccurrenceEngagementRow({
  occurrenceId,
  engagement,
  onOpenComments,
  onError,
  className = "",
}) {
  const likePending = useIsOccurrenceLikePending(occurrenceId);
  const reactionPending = useIsOccurrenceReactionPending(occurrenceId);

  const canEngage = engagement.can_engage;
  // After a cancellation a like can still be taken back.
  const canToggleLike = canEngage || engagement.is_liked;
  const myReaction = REACTIONS.find((r) => r.type === engagement.my_reaction);
  const likes = engagement.like_count || 0;
  const comments = engagement.comment_count || 0;

  const report = (err, action) => {
    if (onError) onError(err, action);
    else console.error(`Failed to ${action}:`, err);
  };

  const toggleLike = () => {
    if (!canToggleLike || likePending) return;
    occurrenceEngagementStore
      .setLiked(occurrenceId, !engagement.is_liked)
      .catch((err) => report(err, "update your like"));
  };

  const react = async (reactionType) => {
    try {
      return await occurrenceEngagementStore.react(occurrenceId, reactionType);
    } catch (err) {
      report(err, "update your reaction");
      throw err;
    }
  };

  const commentLabel = `${comments} comment${comments === 1 ? "" : "s"}`;

  return (
    <div className={`flex justify-between ${className}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="flex space-x-1 items-center">
          <button
            type="button"
            onClick={toggleLike}
            disabled={!canToggleLike || likePending}
            aria-pressed={!!engagement.is_liked}
            aria-label={`${engagement.is_liked ? "Unlike" : "Like"} event, ${likes} like${likes === 1 ? "" : "s"}`}
            className={`flex items-center ${
              canToggleLike ? "text-red-500 hover:text-red-600" : "text-gray-400 cursor-not-allowed"
            }`}
          >
            <Heart fill={engagement.is_liked ? "currentColor" : "none"} aria-hidden="true" />
          </button>
          <span aria-hidden="true">{likes}</span>
        </div>

        <div className="flex space-x-1 items-center">
          {onOpenComments ? (
            <button type="button" onClick={onOpenComments} aria-label={`View comments, ${commentLabel}`}>
              <MessageCircle aria-hidden="true" />
            </button>
          ) : (
            <MessageCircle aria-label={commentLabel} role="img" />
          )}
          <span aria-hidden="true">{comments}</span>
        </div>

        <ReactionCounts reactionCounts={engagement.reaction_counts} />
      </div>

      <div className="flex items-center gap-2">
        {canEngage ? (
          <ReactionPickerButton
            myReaction={engagement.my_reaction}
            onSelect={react}
            onReacted={() => {}}
            disabled={reactionPending}
          />
        ) : (
          myReaction && (
            <button
              type="button"
              onClick={() => react(myReaction.type).catch(() => {})}
              disabled={reactionPending}
              className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-60"
            >
              Remove your {myReaction.emoji} reaction
            </button>
          )
        )}
      </div>
    </div>
  );
}
